import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Building2,
  CheckCircle,
  Shield,
  Users,
  Award,
  TrendingUp,
  ChevronRight,
  ChevronLeft,
  LogOut,
  Sparkles,
  Loader
} from 'lucide-react'
import toast from 'react-hot-toast'
import Logo from '../../components/common/Logo'

// Import compressed step components
import CompanyDetailsForm from './steps/CompanyDetailsForm'
import { authService } from '../../services/authService'
import { companyService } from '../../services/companyService'
import CompanyProfileBuilding from './steps/CompanyProfileBuilding'
import SubscriptionPlan from '../../components/common/SubscriptionPlan'

const CompanyRegistration = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)
  const [paymentCompleted, setPaymentCompleted] = useState(false)
  const [userId, setUserId] = useState(null)
  const [companyId, setCompanyId] = useState(null)
  const [userData, setUserData] = useState(null)
  const navigate = useNavigate()
  const formRef = useRef()

  // Get user data from sessionStorage on mount
  useEffect(() => {
    const loadUserData = async () => {
      setIsCheckingStatus(true)
      const user = await authService.getCurrentUser()
      console.log('Retrieved user from authService:', user)

      if (user) {
        const userId = user.id || user.user_id
        setUserId(userId)
        setUserData(user)
        sessionStorage.setItem('userId', userId)
        sessionStorage.setItem('userData', JSON.stringify(user))
        console.log('User ID loaded:', userId)
        console.log('Full user data:', user)

        // Check registration status to determine starting step
        const registrationStatus = user.hasCompletedRegistration
        console.log('User registration status:', registrationStatus)

        // Check multiple formats for hasCompletedRegistration
        const hasCompletedRegistration =
          registrationStatus === true ||
          registrationStatus === "true" ||
          registrationStatus === 1 ||
          registrationStatus === '1' ||
          String(registrationStatus).toLowerCase() === 'true'

        if (hasCompletedRegistration) {
          // User has completed registration, redirect to dashboard immediately
          console.log('Registration completed, redirecting to dashboard...')
          setIsCheckingStatus(false)
          requestAnimationFrame(() => {
            navigate('/dashboard/company', { replace: true })
          })
          return
        } else if (registrationStatus === 'company-profile') {
          // User has completed profile step, start from subscription step
          setCurrentStep(3)
          setIsCheckingStatus(false)
        } else if (registrationStatus === 'company-register') {
          // User has completed company details step, start from profile building step
          setCurrentStep(2)
          setIsCheckingStatus(false)
        } else {
          // User hasn't started or is at the beginning, start from step 1
          setCurrentStep(1)
          setIsCheckingStatus(false)
        }
      } else {
        console.warn('No user found in sessionStorage or authService')
        const storedUserId = sessionStorage.getItem('userId')
        const storedUserData = sessionStorage.getItem('userData')

        if (storedUserId) {
          setUserId(storedUserId)
          console.log('User ID loaded from sessionStorage:', storedUserId)
        }

        if (storedUserData) {
          try {
            const parsedUserData = JSON.parse(storedUserData)
            setUserData(parsedUserData)
            console.log('User data loaded from sessionStorage:', parsedUserData)

            // Check registration status from stored data
            const registrationStatus = parsedUserData.hasCompletedRegistration
            const hasCompletedRegistration =
              registrationStatus === true ||
              registrationStatus === "true" ||
              registrationStatus === 1 ||
              registrationStatus === '1' ||
              String(registrationStatus).toLowerCase() === 'true'

            if (hasCompletedRegistration) {
              console.log('Registration completed (from stored data), redirecting to dashboard...')
              setIsCheckingStatus(false)
              requestAnimationFrame(() => {
                navigate('/dashboard/company', { replace: true })
              })
              return
            }
          } catch (e) {
            console.error('Error parsing stored user data:', e)
          }
        }
        setIsCheckingStatus(false)
      }

      // Load companyId from sessionStorage if available
      const storedCompanyId = sessionStorage.getItem('companyId')
      if (storedCompanyId) {
        setCompanyId(storedCompanyId)
        console.log('Company ID loaded from sessionStorage:', storedCompanyId)
      }
    }

    loadUserData()
  }, [navigate])

  const steps = [
    {
      id: 1,
      title: 'Company Details',
      description: 'Basic information & verification',
      icon: <Building2 className="w-6 h-6" />,
      component: CompanyDetailsForm,
      color: 'from-blue-500 to-cyan-500',
      apiMethod: companyService.saveCompanyDetails
    },
    {
      id: 2,
      title: 'Profile Building',
      description: 'Company profile and branding',
      icon: <Shield className="w-6 h-6" />,
      component: CompanyProfileBuilding,
      color: 'from-purple-500 to-violet-500',
      apiMethod: companyService.saveCompanyProfile
    },
    {
      id: 3,
      title: 'Subscription',
      description: 'Choose your plan & payment',
      icon: <CheckCircle className="w-6 h-6" />,
      component: SubscriptionPlan,
      color: 'from-orange-500 to-red-500',
      apiMethod: companyService.processCompanySubscription
    }
  ]

  const benefits = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Access Top Talent",
      description: "Connect with pre-vetted professionals and skilled resources"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Grow Your Team",
      description: "Scale your workforce with qualified bench resources"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure Platform",
      description: "Enterprise-grade security and compliance management"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Quality Assurance",
      description: "Pre-verified resources with comprehensive skill assessments"
    }
  ]


  // Handle step completion with API call
  const handleStepComplete = async (stepData) => {
    try {
      setIsLoading(true)
      console.log(`=== STEP ${currentStep} COMPLETION STARTED ===`)
      console.log(`Completing step ${currentStep} with data:`, stepData)

      const currentStepConfig = steps[currentStep - 1]
      console.log(`Current step config:`, currentStepConfig)

      if (currentStepConfig.apiMethod) {
        console.log(`Calling API for step ${currentStep}:`, currentStepConfig.apiMethod.name)

        let apiData = { ...stepData }

        // Enhance data with IDs for different steps
        if (currentStep === 1) {
          apiData = {
            ...stepData,
            user_id: userId || await getUserIdFromStorage(),
            user_type: 'company'
          }
        } else if (currentStep === 2) {
          // Step 2 is for profile building
          apiData = {
            ...stepData,
            company_id: companyId || getCompanyIdFromStorage(),
            user_id: userId || await getUserIdFromStorage()
          }
          console.log('Enhanced profile data for API:', apiData)
        } else if (currentStep === 3) {
          // Step 3 is for subscription/payment
          apiData = {
            ...stepData,
            company_id: companyId || getCompanyIdFromStorage(),
            user_id: userId || await getUserIdFromStorage(),
            plan_name: stepData.plan_name || stepData.selectedPlan,
            billing_cycle: stepData.billing_cycle || stepData.billingCycle || 'Monthly'
          }
          console.log('Enhanced subscription data for API:', apiData)
        }


        const result = await currentStepConfig.apiMethod(apiData)

        // Handle step-specific success actions
        if (currentStep === 1) {
          const newCompanyId = result?.company_id || result?.data?.company_id
          if (newCompanyId) {
            setCompanyId(newCompanyId)
            console.log('Company ID received:', newCompanyId)
            sessionStorage.setItem('companyId', newCompanyId)
          }
        } else if (currentStep === 2) {
          console.log('Profile building completed, proceeding to subscription step')
          // No payment completion needed for step 2
        } else if (currentStep === 3) {
          console.log('Subscription completed, proceeding to finalize registration')
          setPaymentCompleted(true)
          await handleCompleteRegistration(stepData)
          return // Don't proceed to next step since we're completing registration
        }

        // Move to next step if not the last step
        if (currentStep < steps.length) {
          console.log(`Moving to next step: ${currentStep + 1}`)
          setCurrentStep(currentStep + 1)
        }

        console.log(`Step ${currentStep} data saved successfully`)

        // Update user registration status in sessionStorage
        const userDataString = sessionStorage.getItem('userData')
        if (userDataString) {
          const userData = JSON.parse(userDataString)
          let newStatus = userData.hasCompletedRegistration

          if (currentStep === 1) {
            newStatus = 'company-register'
          } else if (currentStep === 2) {
            newStatus = 'company-profile'
          } else if (currentStep === 3) {
            newStatus = true // Fully completed
          }

          userData.hasCompletedRegistration = newStatus
          sessionStorage.setItem('userData', JSON.stringify(userData))
          // Also store hasCompletedRegistration separately for easier access
          sessionStorage.setItem('hasCompletedRegistration', JSON.stringify(newStatus))
          console.log(`Updated user registration status to: ${newStatus}`)
        }

        toast.success(`${currentStepConfig.title} saved successfully!`)

      } else {
        console.log(`No API method defined for step ${currentStep}`)
        // Move to next step if no API method
        if (currentStep < steps.length) {
          setCurrentStep(currentStep + 1)
        } else {
          // If it's the last step and no API method, complete registration
          await handleCompleteRegistration(stepData)
        }
      }

      console.log(`=== STEP ${currentStep} COMPLETION FINISHED ===`)

    } catch (error) {
      console.error(`=== ERROR in step ${currentStep} ===`)
      console.error('Error details:', error)
      console.error('Error message:', error.message)
      console.error('Error stack:', error.stack)

      toast.error(error.message || `Failed to save ${steps[currentStep - 1]?.title}. Please try again.`)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Add helper functions to get IDs from storage
  const getUserIdFromStorage = async () => {
    const user = await authService.getCurrentUser()
    return user?.id || user?.user_id || sessionStorage.getItem('userId')
  }

  const getCompanyIdFromStorage = () => {
    return companyId || sessionStorage.getItem('companyId')
  }

  // Function to trigger form submission from the Continue button
  const triggerFormSubmission = () => {
    console.log('Triggering form submission for step:', currentStep)

    if (formRef.current && formRef.current.submitForm) {
      console.log('Calling form submit via ref')
      formRef.current.submitForm()
    } else {
      console.log('No form ref available, trying to find form element')
      const form = document.querySelector('form')
      if (form) {
        console.log('Form found, checking validation')
        const requiredFields = form.querySelectorAll('[required]')
        let allValid = true

        requiredFields.forEach(field => {
          if (!field.value.trim()) {
            allValid = false
            field.focus()
            toast.error(`Please fill in ${field.labels?.[0]?.textContent || 'this required field'}`)
          }
        })

        if (allValid) {
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true })
          form.dispatchEvent(submitEvent)
        }
      } else {
        console.log('No form found')
        toast.error('Please fill in all required fields')
      }
    }
  }

  // Function to handle complete registration after approval
  const handleCompleteRegistration = async (approvalData) => {
    try {
      setIsLoading(true)
      console.log('Completing company registration...')

      const registrationData = {
        companyName: formData.company_name || formData.companyName || formData.legal_entity_name || 'Company Name',
        email: formData.email || formData.corporate_email || formData.contactPerson?.email || 'email@company.com',
        selectedPlan: formData.plan_name || formData.selectedPlan,
        status: 'approved',
        registrationDate: new Date().toISOString(),
        companyId: companyId,
        userId: userId
      }

      sessionStorage.setItem('companyRegistrationData', JSON.stringify(registrationData))
      sessionStorage.setItem('companyId', companyId)
      sessionStorage.setItem('userId', userId)

      console.log('Registration data stored:', registrationData)
      toast.success('Company registration completed! Redirecting to document upload...')

      setTimeout(() => {
        navigate('/company/document-upload')
      }, 1500)

    } catch (error) {
      console.error('Error completing registration:', error)
      toast.error('Error completing registration. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Add the missing handleLogout function
  const handleLogout = async () => {
    try {
      await authService.logout()
      navigate('/')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const CurrentStepComponent = steps[currentStep - 1]?.component

  // Show loading screen while checking registration status
  if (isCheckingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-100 dark:from-gray-900 dark:via-purple-900 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 mx-auto mb-4 animate-spin text-primary-600" />
          <p className="text-gray-600 dark:text-gray-400">Checking registration status...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-100 dark:from-gray-900 dark:via-purple-900 dark:to-slate-900">
      {/* Header */}
      <header className="bg-white/80 dark:bg-black/20 backdrop-blur-xl border-b border-blue-200/50 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <Logo />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-900 to-cyan-800 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                AJA Bench
              </span>
            </Link>

            {/* Progress Indicator */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-blue-700 dark:text-gray-300">Step {currentStep} of {steps.length}</span>
                <div className="w-32 bg-blue-200/50 dark:bg-white/10 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-500 dark:to-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentStep - 1) / steps.length) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Right side buttons */}
            <div className="flex items-center space-x-3">
              {/* Logout Button */}
              <motion.button
                onClick={handleLogout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors duration-200 border border-red-200 dark:border-red-800"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </motion.button>

            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Sidebar - Progress & Benefits */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Progress Steps */}
              <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-blue-200/50 dark:border-white/10">
                <h3 className="text-lg font-semibold text-blue-900 dark:text-white mb-6">Registration Progress</h3>
                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <div key={step.id} className="flex items-center space-x-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${currentStep > step.id
                        ? 'bg-green-500 text-white'
                        : currentStep === step.id
                          ? `bg-gradient-to-r ${step.color} text-white`
                          : 'bg-blue-200/50 dark:bg-white/10 text-blue-600 dark:text-gray-400'
                        }`}>
                        {currentStep > step.id ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <span className="text-sm font-semibold">{step.id}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${currentStep >= step.id ? 'text-blue-900 dark:text-white' : 'text-blue-600 dark:text-gray-400'
                          }`}>
                          {step.title}
                        </p>
                        <p className="text-xs text-blue-600 dark:text-gray-500">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-blue-200/50 dark:border-white/10">
                <h3 className="text-lg font-semibold text-blue-900 dark:text-white mb-6">Why Join AJA Bench?</h3>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start space-x-3"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-500 dark:to-purple-600 rounded-lg flex items-center justify-center">
                        {benefit.icon}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-blue-900 dark:text-white">{benefit.title}</h4>
                        <p className="text-xs text-blue-700 dark:text-gray-400">{benefit.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Support */}
              <div className="bg-gradient-to-r from-blue-100/80 to-cyan-100/80 dark:from-blue-500/10 dark:to-purple-600/10 backdrop-blur-sm rounded-2xl p-6 border border-blue-200/50 dark:border-blue-500/20">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-500 dark:to-purple-600 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-blue-900 dark:text-white">Need Help?</h3>
                </div>
                <p className="text-sm text-blue-700 dark:text-gray-300 mb-4">
                  Our support team is here to help you through the registration process.
                </p>
                <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium">
                  Contact Support →
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-blue-200/50 dark:border-white/10 overflow-hidden">
              {/* Step Header */}
              <div className="bg-gradient-to-r from-blue-100/80 to-cyan-100/80 dark:from-blue-500/10 dark:to-purple-600/10 p-6 border-b border-blue-200/50 dark:border-white/10">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${steps[currentStep - 1]?.color} rounded-xl flex items-center justify-center`}>
                    {steps[currentStep - 1]?.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-blue-900 dark:text-white">
                      {steps[currentStep - 1]?.title}
                    </h2>
                    <p className="text-blue-700 dark:text-gray-300">
                      {steps[currentStep - 1]?.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {CurrentStepComponent && (
                      <CurrentStepComponent
                        ref={formRef}
                        formData={formData}
                        onNext={handleStepComplete}
                        onPrevious={() => currentStep > 1 && setCurrentStep(currentStep - 1)}
                        isLoading={isLoading}
                        userType="company"
                        userId={userId}
                        companyId={companyId}
                        userData={userData}
                        setParentPaymentCompleted={setPaymentCompleted}
                        setFormData={setFormData}
                      />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation Footer */}
              <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm p-6 border-t border-blue-200/50 dark:border-white/10">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => currentStep > 1 && setCurrentStep(currentStep - 1)}
                    disabled={currentStep === 1 || isLoading}
                    className="flex items-center space-x-2 px-4 py-2 text-blue-700 dark:text-gray-300 hover:text-blue-900 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span>Previous</span>
                  </button>

                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {steps.map((_, index) => (
                        <div
                          key={index}
                          className={`w-2 h-2 rounded-full transition-colors ${index + 1 <= currentStep
                            ? 'bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-500 dark:to-purple-600'
                            : 'bg-blue-200/50 dark:bg-white/20'
                            }`}
                        />
                      ))}
                    </div>
                    <div className="text-sm text-blue-600 dark:text-gray-400">
                      {currentStep} of {steps.length}
                    </div>
                  </div>

                  {/* Continue Button - FIXED: Removed paymentCompleted check for step 2 */}
                  <button
                    onClick={triggerFormSubmission}
                    disabled={isLoading}
                    className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-500 dark:to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 dark:hover:from-blue-600 dark:hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {currentStep === steps.length ? 'Completing...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        <span>
                          {currentStep === steps.length
                            ? 'Complete Registration'
                            : `Continue to ${steps[currentStep]?.title}`
                          }
                        </span>
                        <ChevronRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompanyRegistration